<script setup lang="ts">
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputNumber from "primevue/inputnumber";
import { ref, PropType, onMounted, computed } from "vue";
import { DirectLink, Participant } from "../../../types/PersologOnlineTypes.ts";
import { parseServerDate } from "@persolog/utils";
import DirectLinkExpiryInput from "./DirectLinkExpiryInput.vue";

const { t } = useI18n();

/**
 * @property {Participant[]} participants - The participants to assign the direct link to.
 * @property {number} availableQuantity - The available quantity of direct links.
 * @property {boolean} create - Whether the dialog is in create mode.
 * @property {boolean} edit - Whether the dialog is in edit mode.
 */
const props = defineProps({
    participants: {
        type: Array as PropType<Participant[]>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    availableQuantity: {
        type: Number as PropType<number>,
        required: true,
    },
    create: {
        type: Boolean,
        default: false,
    },
    edit: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["create:directlink", "edit:directlink", "close:dialog"]);

/**
 * Initial value for the newAmount is existing direct link quantity.
 * If the project has no direct link assigned, the newAmount initial value
 * is set to the amount of participants.
 */
const newAmount = ref(props.directLink ? props.directLink.quantity : props.participants.length);

/**
 * How many direct links are already assigned to the project.
 */
const directLinkAssignedAmount = ref(props.directLink?.quantity ?? 0);

const difference = computed(() => newAmount.value - directLinkAssignedAmount.value);

/**
 * Optional point in time at which the direct link is deleted automatically.
 */
const expiresAt = ref<Date | undefined>(
    props.directLink?.expiresAt ? parseServerDate(props.directLink.expiresAt) : undefined,
);

// The date picker's clear button yields null, so normalise both "empty" values for the backend.
const expiresAtForServer = () => expiresAt.value?.toISOString() ?? null;

/**
 * Participants who have already been "used" (past CREATED status).
 * When converting to a direct link project these slots will be occupied,
 * so they must be added to the available ceiling.
 */
const usedParticipantsCount = computed(() =>
    props.participants.filter((p) => p.status !== "CREATED").length,
);

/**
 * The project overall available quantity is the product available quantity
 * plus the direct link assigned amount for the current project.
 * In create mode the already-used participants count must also be added,
 * since those slots will be occupied in the direct link from the start.
 */
const projectAvailableQuantity = computed(() => {
    if (directLinkAssignedAmount.value === undefined) {
        // Create mode: ceiling = already-occupied slots + profiles still available
        return usedParticipantsCount.value + props.availableQuantity;
    }
    // Edit mode: ceiling = current contingent + profiles still available
    return props.availableQuantity + directLinkAssignedAmount.value;
});

const visible = ref(true);

const closeDialog = () => {
    visible.value = false;
    emit("close:dialog");
};

/**
 * Emits the createDirectLink event with the assigned newAmount of direct link contingent and the
 * optional expiry.
 */
function createDirectLink() {
    emit("create:directlink", newAmount.value, expiresAtForServer());
}

/**
 * Emits the editDirectLink event with the current newAmount value and the optional expiry.
 */
const editDirectLink = () => {
    emit("edit:directlink", newAmount.value, expiresAtForServer());
};

/**
 * Handles the confirm button click.
 */
const handleConfirm = () => {
    if (props.create) {
        createDirectLink();
    } else if (props.edit) {
        editDirectLink();
    }
    closeDialog();
};

onMounted(() => {
    if (props.create && props.edit) {
        throw new Error('Either "edit" or "create" must be set, but not both.');
    }
});
</script>

<template>
    <!-- Direct link selection dialog -->
    <Dialog
        v-model:visible="visible"
        modal
        :header="create ? t('project-configuration.directLink.create') : t('project-configuration.tabs.configureDirectLink')"
        :style="{ width: '30rem' }"
        @on-hide="closeDialog"
        :closable="false"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <p class="text-sm mb-2">
            {{ t("project-configuration.directLinkAssignment", { quantity: directLinkAssignedAmount }) }}
        </p>
        <div class="flex-auto gap-4 mb-4">
            <label for="direct-link-edit-dialog_input-number_contingent" class="text-sm font-bold block mb-2"> 
                {{ t("project-configuration.directLinkAssignmentChanged", { quantity: directLinkAssignedAmount }) }}     
            </label>
            <InputNumber
                id="direct-link-edit-dialog_input-number_contingent"
                input-id="direct-link-edit-dialog_input-number_contingent"
                show-buttons
                button-layout="horizontal"
                v-model="newAmount"
                :step="1"
                mode="decimal"
                :min="props.participants.length"
                :max="projectAvailableQuantity"
            >
                <template #incrementicon>
                    <span class="pi pi-plus" />
                </template>
                <template #decrementicon>
                    <span class="pi pi-minus" />
                </template>
            </InputNumber>
        </div>
        <div v-if="difference !== 0" :class="['text-sm mt-1 mb-1 font-medium', difference > 0 ? 'text-green-600' : 'text-red-600']"
        >
          ({{ t("project-configuration.directLinkAssignmentDifference") }}{{ difference > 0 ? '+' : '' }}{{ difference }})
        </div>
        <small class="flex items-center mb-6">
            <i class="pi pi-exclamation-circle mr-1"></i>
            {{ t("project-configuration.directLinkContingentExplained", { quantity: projectAvailableQuantity }) }}
        </small>
        <DirectLinkExpiryInput
            v-model="expiresAt"
            input-id="direct-link-edit-dialog_input-datepicker_expiry"
            class="mb-8"
        />
        <div class="flex justify-end gap-2">
            <Button type="button" :label="t('buttons.cancel')" severity="secondary" @click="closeDialog"></Button>
            <Button id="direct-link-edit-dialog_button_ok" type="button" :disabled="newAmount == 0" :label="t('buttons.ok')" @click="handleConfirm"> </Button>
        </div>
    </Dialog>
</template>
